import { z } from 'zod'

/** Version of the consent wording the apps show; bump it whenever that wording changes. */
export const CONSENT_TEXT_VERSION = '2026-10-09'

export const consentStateSchema = z.enum(['granted', 'declined', 'not_asked'])

export const consentDecisionSchema = z.enum(['granted', 'declined'])

export const consentsResponseSchema = z.object({
  /** The consent wording version the server currently asks with. */
  version: z.string(),
  /** Remembering what someone calls places, roughly where they are, and future taste signals. */
  personalization: consentStateSchema,
  /** Anonymized use to improve suggestions for everyone, including future models. */
  modelImprovement: consentStateSchema,
  /** The AGB and Datenschutz version the person last accepted, or null when never. */
  termsAcceptedVersion: z.string().nullable().default(null),
})

/** Records one or both decisions; each becomes a new row in the person's consent history. */
export const updateConsentsRequestSchema = z
  .object({
    /** The consent wording version the person was shown. */
    version: z.string().min(1).max(32),
    personalization: consentDecisionSchema.optional(),
    modelImprovement: consentDecisionSchema.optional(),
  })
  .refine((body) => body.personalization !== undefined || body.modelImprovement !== undefined, {
    message: 'Decide at least one purpose',
  })

export type ConsentState = z.infer<typeof consentStateSchema>
export type ConsentDecision = z.infer<typeof consentDecisionSchema>
export type ConsentsResponse = z.infer<typeof consentsResponseSchema>
export type UpdateConsentsRequest = z.infer<typeof updateConsentsRequestSchema>

/** Version of the AGB and Datenschutz wording the apps link to; bump it whenever they change. */
export const TERMS_VERSION = '2026-10-23'

/** The age a person confirms at a Live Room's door unless their country sets another one. */
export const DOOR_CONFIRMED_AGE = 18

/**
 * The three yeses at a Live Room's door, all required: old enough, AGB and Datenschutz accepted,
 * and others here may see the person and say Hi. Checking in with them goes open until the end.
 */
export const roomConsentSchema = z.object({
  /** "Ich bin 18 oder älter", or the age shown in `confirmedAge`. */
  ageConfirmed: z.literal(true),
  /** The age the person confirmed being at least; 18 when left out. */
  confirmedAge: z.number().int().min(14).max(21).optional(),
  /** "Ich akzeptiere AGB und Datenschutz". */
  termsAccepted: z.literal(true),
  /** The AGB and Datenschutz version the person was shown, normally `TERMS_VERSION`. */
  termsVersion: z.string().min(1).max(32),
  /** "Andere hier dürfen mich sehen und mir Hi sagen". */
  visibleAndHi: z.literal(true),
})
export type RoomConsent = z.infer<typeof roomConsentSchema>
